import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaChartLine, FaTrophy } from 'react-icons/fa';
import ModalComponent from '../components/ModalComponent';
import api from '../api/axiosInstance';
import { RootState } from '../store/store';

// Re-show cadence: once, then again after ~30 days (stores the last-shown timestamp).
const SEEN_KEY = 'seenProfitModal';
const RESHOW_MS = 30 * 24 * 60 * 60 * 1000;

// Minimal shape of a tip from GET /api/betting-tips — the SAME data source the
// /databaza-tipov page (BettingTipsList) uses. Status values are Slovak: "vyhraté" / "prehraté".
interface Bet {
    odds: number;
    stake: number;
    status: string;
}

// --- Design lifted from BettingTipsList (the database-of-tips page) so the popup matches it. ---
const CardStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const StatCard = styled.div`
    background: linear-gradient(145deg, #1a1a1a, #2d2d2d);
    padding: 20px;
    border-radius: 15px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    color: white;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const UnitsCard = styled(StatCard)`
    background: linear-gradient(135deg, #11a80b, #00ff04);
`;

const StatValue = styled.div`
    font-size: 2.2em;
    font-weight: 800;
    margin: 10px 0;
    display: flex;
    align-items: center;
    gap: 10px;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const StatLabel = styled.div`
    font-size: 1.1em;
    color: rgba(255, 255, 255, 0.85);
    text-align: center;
    font-weight: 300;
`;

const ProgressBar = styled.div<{ width: number }>`
    width: 100%;
    height: 6px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    margin-top: 10px;
    overflow: hidden;

    &::after {
        content: '';
        display: block;
        width: ${props => props.width}%;
        height: 100%;
        background: #f56607;
        border-radius: 4px;
        transition: width 0.5s ease-in-out;
    }
`;

const Cta = styled(Link)`
    display: block;
    margin-top: 4px;
    padding: 12px 24px;
    background: #f56607;
    color: #fff;
    font-weight: 700;
    text-align: center;
    border-radius: 10px;
    text-decoration: none;

    &:hover {
        background: #d95605;
    }
`;

/**
 * One-time landing popup that brags about the public betting-tips track record.
 * Shown only to logged-out visitors, at most once per 30 days. Reuses the SAME public
 * data (GET /api/betting-tips/public — the settled-tips-only projection, see the §1.3 security
 * fix note on BettingTipsList.tsx; GET /api/betting-tips itself now requires PREMIUM/ADMIN) and the
 * SAME stat-card design as the /databaza-tipov page, and computes net units + win rate with the
 * same formula so the numbers match exactly.
 */
const ProfitModal: React.FC = () => {
    const accessToken = useSelector((state: RootState) => state.auth.accessToken);
    const [bets, setBets] = useState<Bet[] | null>(null);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        // Logged-out visitors only.
        if (accessToken) {
            return;
        }
        // Respect the 30-day cadence.
        const lastShown = localStorage.getItem(SEEN_KEY);
        if (lastShown && Date.now() - Number(lastShown) < RESHOW_MS) {
            return;
        }

        let cancelled = false;
        api.get<Bet[]>('/betting-tips/public')
            .then((res) => {
                if (cancelled) {
                    return;
                }
                const data = res.data || [];
                if (data.length === 0) {
                    return; // nothing to brag about yet — don't show, don't burn the cadence
                }
                setBets(data);
                setIsOpen(true);
                localStorage.setItem(SEEN_KEY, String(Date.now()));
            })
            .catch((err) => {
                console.error('Profit stats fetch failed:', err);
            });

        return () => {
            cancelled = true;
        };
    }, [accessToken]);

    // Same formula as BettingTipsList.tsx (net units + win rate) so the numbers match the database page.
    const stats = useMemo(() => {
        const list = bets || [];
        const total = list.length;
        const wins = list.filter((b) => b.status === 'vyhraté').length;
        const netUnits = list.reduce((acc, b) => {
            if (b.status === 'vyhraté') {
                return acc + (b.odds - 1) * b.stake;
            }
            if (b.status === 'prehraté') {
                return acc - b.stake;
            }
            return acc;
        }, 0);
        const winRate = total > 0 ? (wins / total) * 100 : 0;
        return { total, wins, netUnits, winRate };
    }, [bets]);

    if (!bets) {
        return null;
    }

    return (
        <ModalComponent isOpen={isOpen} onClose={() => setIsOpen(false)} title="Naša databáza tipov">
            <CardStack>
                <UnitsCard>
                    <FaChartLine size={24} color="#fff" />
                    <StatValue>
                        {stats.netUnits >= 0 ? '+' : ''}
                        {stats.netUnits.toFixed(2)}
                    </StatValue>
                    <StatLabel>Vyhraté jednotky</StatLabel>
                </UnitsCard>

                <StatCard>
                    <FaTrophy size={24} color="#28a745" />
                    <StatValue>
                        {stats.wins}
                        <span style={{ fontSize: '0.6em', color: 'rgba(255,255,255,0.7)' }}>
                            {' / '}
                            {stats.total}
                        </span>
                    </StatValue>
                    <StatLabel>Vyhraté tipy</StatLabel>
                    <ProgressBar width={stats.winRate} />
                </StatCard>

                <Cta to="/databaza-tipov" onClick={() => setIsOpen(false)}>
                    Pozri celú databázu →
                </Cta>
            </CardStack>
        </ModalComponent>
    );
};

export default ProfitModal;
